import type { FormFieldConfig, FormSectionConfig } from '@/core/components'

// ─── Member shipping form fields ─────────────────────────────────────────────
// Labels use translate keys — rendered via checkout-field-renderer which calls translate()
export const memberShippingFields: FormFieldConfig[] = [
    { key: 'fullname', label: 'full_name', type: 'text', row: 1 },
    { key: 'email', label: 'email_address', type: 'text', row: 1 },
    { key: 'phone', label: 'phone', type: 'phone', row: 2 },
    { key: 'country', label: 'country', type: 'country', row: 2, defaultValue: 'OM' },
    { key: 'address1', label: 'address', type: 'address', row: 3 },
    { key: 'address2', label: 'address2', type: 'address', row: 3, optional: true },
    { key: 'city', label: 'city', type: 'text', row: 4 },
    { key: 'state', label: 'state', type: 'select', row: 4 }
]

export const pickupShippingFields: FormFieldConfig[] = [
    { key: 'fullname', label: 'full_name', type: 'text', row: 1 },
    { key: 'email', label: 'email_address', type: 'text', row: 1 },
    { key: 'phone', label: 'phone', type: 'phone', row: 2 },
    { key: 'country', label: 'country', type: 'country_readonly', row: 2, defaultValue: 'OM' }
]

// ─── Guest create-account form fields (sectioned) ────────────────────────────
export const guestAccountSections: FormSectionConfig[] = [
    {
        title: 'signup_shipping',
        fields: [
            { key: 'firstname', label: 'first_name', type: 'text', row: 1 },
            { key: 'lastname', label: 'last_name', type: 'text', row: 1 },
            { key: 'email', label: 'email_address', type: 'text', row: 2 },
            { key: 'phone', label: 'phone', type: 'phone', row: 2 },
            { key: 'password', label: 'password', type: 'password', row: 3 },
            { key: 'country', label: 'country', type: 'country', row: 3, defaultValue: 'OM' },
            { key: 'address1', label: 'address', type: 'address', row: 4 },
            { key: 'address2', label: 'address2', type: 'address', row: 4, optional: true },
            { key: 'city', label: 'city', type: 'text', row: 5 },
            { key: 'state', label: 'state', type: 'select', row: 5 }
        ]
    }
]

export const guestAgreementFields: FormFieldConfig[] = [
    { key: 'pdpa', label: 'i_agree_unicity_member', type: 'checkbox', row: 1, defaultValue: false, linkText: 'terms_conditions_feelgreat' },
    { key: 'terms', label: 'form_signup_privacy_label', type: 'checkbox', row: 2, defaultValue: false }
]

export const creditCardFields: FormSectionConfig[] = [
    {
        // 'credit_card' — Oman is 3DS, no stored card, so no "new" (USHOP-3078).
        title: 'credit_card',
        fields: [
            { key: 'name', label: 'name_on_card', type: 'text', row: 1 },
            { key: 'card_number', label: 'card_number', type: 'card_number', row: 1 },
            { key: 'exp_date', label: 'exp_date', type: 'exp_date', row: 2 },
            { key: 'cvv', label: 'cvv', type: 'cvv', row: 2 }
        ]
    }
]

// ─── Billing address fields (credit card) ────────────────────────────────────
export const billingAddressFields: FormFieldConfig[] = [
    { key: 'firstname', label: 'first_name', type: 'text', row: 1 },
    { key: 'country', label: 'country', type: 'country', row: 1, defaultValue: 'OM' },
    { key: 'address1', label: 'address', type: 'address', row: 2 },
    { key: 'address2', label: 'address2', type: 'address', row: 2, optional: true },
    { key: 'city', label: 'city', type: 'text', row: 3 },
    { key: 'state', label: 'state', type: 'select', row: 3 }
]

export const omanConfig = {
    country: 'oman',
    code: '512',
    alpha2: 'OM',
    alpha3: 'OMN',
    currency: { code: 'OMR', symbol: 'OMR' },
    dialCode: '+968',
    languages: ['en', 'ar'],
    defaultLanguage: 'en',
    countryListApiKey: 'country_omn',

    features: {
        auth: { enabled: true },
        products: { enabled: true },
        shipping: {
            enabled: true,
            options: ['delivery', 'pickup'] as const,
            addressFormat: 'standard' as const
        },
        payments: {
            enabled: true,
            methods: ['credit-card', 'bank-wire', 'cash-on-delivery', 'pay-at-pickup'] as const,
            creditCardGateway: 'myfatoorah',
            saveAlias: true
        },
        checkout: {
            taxInvoice: false,
            agreements: ['pdpa', 'terms'] as const,
            recaptcha: true
        },
        subscriptions: { enabled: true },
        orderHistory: { enabled: true }
    },

    vat: { percentage: 0, description: '', type: 'none' as const },

    pickup: {
        name: 'Ahmed AI-Farsi',
        address: '123 AI nahda street',
        postalCode: '112',
        city: 'Muscat',
        country: 'OMAN'
    },

    bankWire: {
        accountName: 'Jawaar Development',
        accountNumber: '0312043238530014',
        swiftCode: 'BMUSOMRXXXX',
        bankName: 'Bank Muscat',
        branchName: 'Mawaleh South',
        branchCode: '312'
    },

    tracking: {
        ga4: '',
        fbPixel: ''
    },

    styles: {
        fontFamily: "'Inter', 'NotoSansThai', sans-serif",
        colors: {
            primary: '#003764',
            primaryLight: '#5A8FC3',
            heading: '#003764',
            text: '#003764',
            textMuted: 'rgb(11, 59, 97)',
            sectionHeading: 'rgb(21, 56, 98)',
            border: 'rgba(153, 188, 223, 0.25)',
            pageBg: 'rgb(209, 217, 226)',
            cardBg: '#ffffff'
        }
    }
} as const
